import { useMemo } from 'react';
import { useWindowSize } from './useWindowSize';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRouter } from './useRouter';
import { useAtomValue } from 'jotai';
import * as analysisState from '@/components/Analysis/state';

// Screen size breakpoints
const BREAKPOINTS = {
  SMALL: 1280,
  MEDIUM: 1728,
};

// Base drawer width configurations
const DRAWER_WIDTHS_CONFIG = {
  [BREAKPOINTS.SMALL]: {
    leftDrawerWidth: 220,
    doublePanelWidth: 540,
    riskDrawerWidth: 220,
  },
  [BREAKPOINTS.MEDIUM]: {
    leftDrawerWidth: 230,
    doublePanelWidth: 580,
    riskDrawerWidth: 230,
  },
  DEFAULT: {
    leftDrawerWidth: 280,
    doublePanelWidth: 828,
    riskDrawerWidth: 280,
  },
};

// Right drawer width configurations
const RIGHT_DRAWER_WIDTHS = {
  STANDARD: {
    [BREAKPOINTS.SMALL]: 240,
    [BREAKPOINTS.MEDIUM]: 260,
    DEFAULT: 328,
  },
  REVIEWER: {
    [BREAKPOINTS.SMALL]: 300,
    [BREAKPOINTS.MEDIUM]: 320,
    DEFAULT: 380,
  },
};

export const useDrawerWidths = () => {
  const sideBySideModeEnabled = useAtomValue(analysisState.sideBySideModeEnabled);
  const drawerRight = useAtomValue(analysisState.drawerRight);
  const screenSize = useWindowSize();
  const router = useRouter();
  const canViewReviewScreen = useFeatureFlag(['api-inspector', 'mi-engineer']);
  const isAtViewerPage = useMemo(() => router.pathname.includes('viewer'), [router.pathname]);

  const reviewerMode = useMemo(
    () => isAtViewerPage && canViewReviewScreen,
    [isAtViewerPage, canViewReviewScreen]
  );

  const halfScreenWidth = useMemo(() => {
    const { width } = screenSize;
    return width ? width / 2 : 0;
  }, [screenSize]);

  const drawerWidths = useMemo(() => {
    const { width } = screenSize;
    const rightDrawerConfig = reviewerMode
      ? RIGHT_DRAWER_WIDTHS.REVIEWER
      : RIGHT_DRAWER_WIDTHS.STANDARD;

    // Determine which configuration to use based on screen width
    let config;
    if (width && width <= BREAKPOINTS.SMALL) {
      config = DRAWER_WIDTHS_CONFIG[BREAKPOINTS.SMALL];
    } else if (width && width <= BREAKPOINTS.MEDIUM) {
      config = DRAWER_WIDTHS_CONFIG[BREAKPOINTS.MEDIUM];
    } else {
      config = DRAWER_WIDTHS_CONFIG.DEFAULT;
    }

    // Determine right drawer width based on mode and screen size
    let rightDrawerWidth;
    if (sideBySideModeEnabled && drawerRight.equipment && !drawerRight.measuringTool) {
      rightDrawerWidth = halfScreenWidth;
    } else if (width && width <= BREAKPOINTS.SMALL) {
      rightDrawerWidth = rightDrawerConfig[BREAKPOINTS.SMALL];
    } else if (width && width <= BREAKPOINTS.MEDIUM) {
      rightDrawerWidth = rightDrawerConfig[BREAKPOINTS.MEDIUM];
    } else {
      rightDrawerWidth = rightDrawerConfig.DEFAULT;
    }

    return {
      ...config,
      rightDrawerWidth,
    };
  }, [
    screenSize,
    reviewerMode,
    sideBySideModeEnabled,
    drawerRight.equipment,
    drawerRight.measuringTool,
    halfScreenWidth,
  ]);

  return drawerWidths;
};
